import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { localization as commonLocalization } from '@sitebuilder/common';
import { types as coreTypes } from '@sitebuilder/core';
import { ResourceApiClient } from '../api/resourceApiClient';
import { templateEditorReducerActions } from './templateEditor';
import { ThunkResult } from '.';

export interface StateShape {
    isShowResourcePicker?: boolean;
    selectedResourcePickerValue?: string | null;
    selectedSchemaId?: string | null;
    resourcePickerValues: coreTypes.ResourcePicker | null;
    resources: coreTypes.Resources | null;
    resourcesLoading: boolean;
    resourcesLoadError: any | null;
    resourcesSaveSuccess?: string;
    resourcesSaveError?: any | null;
    resourcesSaveLoading?: boolean;
    selectedResource: {
        id: string;
        value: string;
        locale: string;
    } | null;
}

export const defaultState: StateShape = {
    isShowResourcePicker: false,
    selectedResourcePickerValue: null,
    selectedSchemaId: null,
    resourcePickerValues: { page: {}, site: {}, mdm: {} },
    resourcesSaveSuccess: '',
    resourcesSaveError: null,
    resourcesSaveLoading: false,
    resources: null,
    resourcesLoading: false,
    resourcesLoadError: null,
    selectedResource: null,
};

const apiClient = new ResourceApiClient();

export const actionCreators = {
    setIsShowResourcePicker:
        (value: boolean): ThunkResult<Promise<void>> =>
        async (dispatch) => {
            dispatch(templateSlice.actions.SET_IS_SHOW_RESOURCE_PICKER(value));
            dispatch(templateSlice.actions.SET_SELECTED_RESOURCEPICKER_VALUE(null));
        },
    setSelectedResourcePickerValue:
        (value: string | null): ThunkResult<Promise<void>> =>
        async (dispatch) => {
            dispatch(templateSlice.actions.SET_SELECTED_RESOURCEPICKER_VALUE(value));
        },
    setselectedSchemaId:
        (value: string | null): ThunkResult<Promise<void>> =>
        async (dispatch) => {
            dispatch(templateSlice.actions.SET_SELECTED_SCHEMA_ID(value));
        },
    getResources: (): ThunkResult<Promise<void>> => {
        return async (dispatch) => {
            dispatch(resourcesReducerActions.RESOURCES_LOAD_START());

            try {
                const resources = await apiClient.getResources();
                dispatch(resourcesReducerActions.RESOURCES_LOAD_END({ resources }));
            } catch (err: any) {
                console.error(err);
                dispatch(
                    resourcesReducerActions.RESOURCES_LOAD_END({
                        error: { message: err.message },
                    })
                );
            }
        };
    },
    setResourcePickerValues: (resources: coreTypes.ResourcePicker): ThunkResult<Promise<void>> => {
        return async (dispatch) => {
            dispatch(resourcesReducerActions.SET_RESOURCE_PICKER_VALUES(resources));
        };
    },
    saveResources: (
        resources: coreTypes.Resources,
        resourcesType: string,
        isAddingNewLocale?: boolean
    ): ThunkResult<Promise<boolean | void>> => {
        return async (dispatch, getState) => {
            const state = getState();
            const locale = state?.templateEditor?.documentMetadata?.locale || 'en-US';
            const appI18n = commonLocalization.getLocalization(locale);

            const accessToken = state.auth.user?.access_token;
            dispatch(resourcesReducerActions.RESOURCES_SAVE_START());

            try {
                if (resourcesType === 'site') {
                    await apiClient.saveResources(resources, {
                        accessToken,
                    });
                } else {
                    await dispatch(templateEditorReducerActions.SAVE_PAGE_RESOURCES(resources));
                }

                dispatch(
                    resourcesReducerActions.RESOURCES_SAVE_END({
                        success: appI18n.t(
                            `${
                                isAddingNewLocale
                                    ? 'locale_save_success'
                                    : 'new_resource_save_success'
                            }`
                        ),
                    })
                );
                return true;
            } catch (err: any) {
                console.error(err);
                dispatch(
                    resourcesReducerActions.RESOURCES_SAVE_END({
                        error: { message: err.message },
                    })
                );
                return false;
            }
        };
    },
    importResources: (file: any): ThunkResult<Promise<boolean | void>> => {
        return async (dispatch, getState) => {
            const state = getState();

            const accessToken = state.auth.user?.access_token;
            dispatch(resourcesReducerActions.RESOURCES_SAVE_START());

            try {
                const data: any = await apiClient.importResources(file, {
                    accessToken,
                });
                if (data?.data) {
                    dispatch(
                        resourcesReducerActions.RESOURCES_LOAD_END({
                            resources: data.data,
                            success: data.message,
                            error: '',
                        })
                    );
                } else {
                    dispatch(
                        resourcesReducerActions.RESOURCES_SAVE_END({
                            error: data.message,
                        })
                    );
                }
            } catch (err: any) {
                dispatch(
                    resourcesReducerActions.RESOURCES_SAVE_END({
                        error: err.message,
                    })
                );
            }
        };
    },
    setSelectedResource: (
        selectedResource: {
            id: string;
            value: string;
            locale: string;
        } | null
    ): ThunkResult<Promise<void>> => {
        return async (dispatch) => {
            dispatch(resourcesReducerActions.SET_SELECTED_RESOURCE(selectedResource));
        };
    },
    setResourcesSaveError: (msg: string | null): ThunkResult<Promise<void>> => {
        return async (dispatch) => {
            dispatch(resourcesReducerActions.RESOURCES_SAVE_END({ error: msg, success: '' }));
        };
    },
};

const templateSlice = createSlice({
    name: 'resources',
    initialState: defaultState,
    reducers: {
        SET_IS_SHOW_RESOURCE_PICKER(draft, action: PayloadAction<boolean>) {
            draft.isShowResourcePicker = action.payload;
        },
        SET_SELECTED_RESOURCEPICKER_VALUE(draft, action: PayloadAction<string | null>) {
            draft.selectedResourcePickerValue = action.payload;
        },
        SET_SELECTED_SCHEMA_ID(draft, action: PayloadAction<string | null>) {
            draft.selectedSchemaId = action.payload;
        },
        RESOURCES_LOAD_START(draft) {
            draft.resourcesLoading = true;
            draft.resources = null;
            draft.resourcesLoadError = null;
        },
        RESOURCES_LOAD_END(
            draft,
            action: PayloadAction<{ resources?: coreTypes.Resources; error?: any; success?: any }>
        ) {
            const { resources, error, success } = action.payload;
            draft.resourcesLoading = false;
            draft.resources = resources || null;
            draft.resourcesLoadError = error;
            draft.resourcesSaveSuccess = success;
        },
        RESOURCES_SAVE_START(draft) {
            draft.resourcesSaveLoading = true;
            draft.resourcesSaveError = null;
            draft.resourcesSaveSuccess = '';
        },
        RESOURCES_SAVE_END(draft, action: PayloadAction<{ success?: string; error?: any }>) {
            const { success, error } = action.payload;
            draft.resourcesSaveLoading = false;
            draft.resourcesSaveSuccess = success || '';
            draft.resourcesSaveError = error;
        },
        SET_RESOURCE_PICKER_VALUES(draft, action: PayloadAction<coreTypes.ResourcePicker>) {
            draft.resourcePickerValues = action.payload;
        },
        SET_SELECTED_RESOURCE(
            draft,
            action: PayloadAction<{
                id: string;
                value: string;
                locale: string;
            } | null>
        ) {
            draft.selectedResource = action.payload;
        },
    },
});

export const resourcesReducerActions: any = templateSlice.actions;
export const resourcesReducer = templateSlice.reducer;
